/**
 * ClassicWidget — widget sidebar accès direct navigation.
 *
 * PHASE 3.0 UX Revolution (DECISIONS #167).
 * V5 : isActive prop — même effet bleu Samsung que SmartWidget.
 */

import { useNavigate } from 'react-router-dom';
import { useAnimate, motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { peauWidget, DecorWidgetActif } from './peauWidget';

export interface ClassicWidgetProps {
  id: string;
  icon: React.ReactNode;
  label: string;
  subLabel: string;
  to: string;
  isActive?: boolean;
}

export function ClassicWidget({ id, icon, label, subLabel, to, isActive = false }: ClassicWidgetProps) {
  const navigate = useNavigate();
  const [scope, animate] = useAnimate();

  function handleNavigate() {
    try {
      void animate('.cw-ripple',
        { scale: [0, 4.5], opacity: [0.4, 0] },
        { duration: 0.52, ease: [0.22, 1, 0.36, 1] },
      );
      void animate('.cw-shimmer', { x: [-90, 280] }, { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] });
      void animate('.cw-arrow',   { x: [0, 6], opacity: [1, 0.4] }, { duration: 0.22, ease: 'easeOut' });
    } catch { /* navigation ne doit jamais être bloquée */ }
    navigate(to);
  }

  return (
    <motion.button
      ref={scope}
      type="button"
      data-testid={`classic-widget-${id}`}
      onClick={handleNavigate}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 600, damping: 35 }}
      className={cn(peauWidget(isActive))}
      aria-pressed={isActive}
    >
      {/* ── Active decorations ───────────────────────────────────────────── */}
      {isActive && (
        <DecorWidgetActif />
      )}

      {/* ── Ripple de lancement ──────────────────────────────────────────── */}
      <span
        className="cw-ripple pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-samsung-400/0 opacity-0"
        aria-hidden
      />

      {/* ── Shimmer clip ─────────────────────────────────────────────────── */}
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl" aria-hidden>
        <span
          className="cw-shimmer absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-transparent via-brand-samsung-400/18 to-transparent"
          style={{ transform: 'translateX(-96px)' }}
        />
      </span>

      {/* ── Header : icône + label + arrow ───────────────────────────────── */}
      <div className="flex items-center justify-between mb-1.5 shrink-0 relative">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn(
              'shrink-0 flex h-[20px] w-[20px] items-center justify-center rounded-md [&_svg]:h-3.5 [&_svg]:w-3.5 transition-all duration-200',
              isActive
                ? 'bg-brand-samsung-500/35 text-brand-samsung-700 dark:text-brand-samsung-200 shadow-[0_0_8px_rgba(20,40,160,0.45)]'
                : 'bg-white/[0.05] text-muted-foreground/40',
            )}
          >
            {icon}
          </span>
          <span className={cn(
            'text-[9px] font-semibold uppercase tracking-widest truncate transition-colors duration-200',
            isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-300/90' : 'text-muted-foreground/70',
          )}>
            {label}
          </span>
        </div>

        {/* Arrow badge */}
        <span className={cn(
          'cw-arrow shrink-0 flex h-[20px] w-[20px] items-center justify-center rounded-md border transition-all duration-200',
          isActive
            ? 'border-brand-samsung-500 bg-brand-samsung-500 text-white shadow-[0_0_8px_rgba(20,40,160,0.4)]'
            : 'border-[var(--border-default,var(--surface-border))] text-muted-foreground/30 group-hover:border-brand-samsung-500 group-hover:bg-brand-samsung-500 group-hover:text-white',
        )}>
          <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>

      {/* ── Sous-label ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center relative">
        <span className={cn(
          'text-[13px] font-bold leading-tight line-clamp-2 transition-colors duration-200',
          isActive ? 'text-brand-samsung-700 dark:text-brand-samsung-100' : 'text-foreground',
        )}>
          {subLabel}
        </span>
      </div>
    </motion.button>
  );
}
